import React, { useRef, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { MAX_PHOTO_SIZE_BYTES, MAX_PHOTO_SIZE_MB } from '../constants';
import { compressImage } from '../lib/compressImage';
import ImageCropDialog from './ImageCropDialog';

// Gallery editor: shows 6 frames; each can be filled with a base64 image.
// Props:
//   photos: string[]  (URLs or data URIs, length 0-6)
//   onChange: (newPhotos: string[]) => void
const GalleryEditor = ({ photos = [], onChange }) => {
  const inputRef = useRef(null);
  const targetIndexRef = useRef(null);
  const [rawSrc, setRawSrc] = useState(null);

  const frames = [...photos];
  while (frames.length < 6) frames.push(null);

  const triggerUpload = (idx) => {
    targetIndexRef.current = idx;
    inputRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_PHOTO_SIZE_BYTES) {
      toast.error('Image too large', { description: `Please choose an image under ${MAX_PHOTO_SIZE_MB}MB. Large phone photos will be auto-resized — just retry.` });
      return;
    }
    // Auto-downscale before cropping so the crop dialog isn't dragging a 10MB
    // canvas around on the user's phone.
    try {
      const compressed = await compressImage(file);
      setRawSrc(compressed);
    } catch (err) {
      toast.error("Couldn't read that image. Try a different one?");
    }
  };

  const handleCropSave = (croppedDataUrl) => {
    const idx = targetIndexRef.current;
    const next = [...frames.slice(0, 6)];
    next[idx] = croppedDataUrl;
    onChange(next.filter(Boolean));
    setRawSrc(null);
  };

  const removeAt = (idx) => {
    const next = frames.slice(0, 6);
    next[idx] = null;
    onChange(next.filter(Boolean));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-white block">Photo Gallery <span className="text-purple-300 text-xs font-normal">(up to 6)</span></label>
        <span className="text-purple-300 text-xs" data-testid="gallery-count">{photos.length} / 6</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {frames.slice(0, 6).map((src, idx) => (
          <div key={src || `empty-${idx}`} className="aspect-square rounded-xl overflow-hidden bg-fnl-purple-light/60 border-2 border-dashed border-purple-700/50 relative group">
            {src ? (
              <>
                <img src={src} alt={`gallery ${idx + 1}`} className="w-full h-full object-cover" data-testid={`gallery-photo-${idx}`} />
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  className="absolute top-1.5 right-1.5 w-7 h-7 bg-black/60 hover:bg-fnl-pink rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`gallery-remove-${idx}`}
                  aria-label={`Remove photo ${idx + 1}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => triggerUpload(idx)}
                className="w-full h-full flex flex-col items-center justify-center text-fnl-pink/70 hover:text-fnl-pink hover:bg-fnl-pink/5 transition-colors"
                data-testid={`gallery-add-${idx}`}
              >
                <Plus className="w-7 h-7 mb-1" />
                <span className="text-[10px] uppercase tracking-wider">Add</span>
              </button>
            )}
          </div>
        ))}
      </div>
      <input
        type="file"
        accept="image/*"
        ref={inputRef}
        onChange={handleFile}
        className="hidden"
        data-testid="gallery-file-input"
      />
      <ImageCropDialog
        open={!!rawSrc}
        src={rawSrc}
        onCancel={() => setRawSrc(null)}
        onSave={handleCropSave}
      />
    </div>
  );
};

export default GalleryEditor;
